import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

// The gesture engine is requested only when a customer opens a photo.
let photoSwipeModule;
const loadPhotoSwipe = () => (photoSwipeModule ??= import('./vendor-photoswipe-5.4.4.esm.min.js').catch((error) => {
  photoSwipeModule = undefined;
  throw error;
}));

/** Fullscreen image viewer, retaining the main gallery's original media indices. */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'sources', 'header', 'footer', 'closeButton', 'zoomButton', 'status', 'fallback', 'fallbackImage'];

  #viewer;
  #opening = 0;
  #closePending = false;
  #index = 0;
  #items = [];
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#items = Array.from(this.refs.sources.content.children, ({ dataset }) => ({
      src: dataset.src,
      srcset: dataset.srcset,
      width: Number(dataset.width),
      height: Number(dataset.height),
      alt: dataset.alt.trim(),
      originalIndex: Number(dataset.index),
    })).filter((item) => item.src && item.width > 0 && item.height > 0);
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.refs.dialog.addEventListener('cancel', this.#cancel, options);
    this.refs.dialog.addEventListener('keydown', this.#keyDown, options);
    // A click inside this nested modal must not dismiss a surrounding quick-add dialog.
    this.refs.dialog.addEventListener('click', (event) => event.stopPropagation(), options);
    this.refs.dialog.addEventListener('close', this.#nativeClose, options);
  }

  disconnectedCallback() {
    this.#opening++;
    this.#listeners?.abort();
    this.#closePending = true;
    if (this.#viewer) {
      this.#viewer.options.hideAnimationDuration = 0;
      this.#viewer.close();
    }
    this.refs.dialog.close();
    this.#syncScrollLock();
    super.disconnectedCallback();
  }

  /** @param {number} originalIndex @param {Event} event */
  async open(originalIndex, event) {
    event?.preventDefault();
    const index = this.#items.findIndex((item) => item.originalIndex === Number(originalIndex));
    if (index < 0 || !this.isConnected) return;
    if (this.#viewer) {
      if (!this.#closePending) this.#viewer.goTo(index);
      return;
    }

    const token = ++this.#opening;
    this.#closePending = false;
    this.#index = index;
    const { dialog, fallback, fallbackImage, closeButton, zoomButton } = this.refs;
    fallback.hidden = false;
    zoomButton.hidden = true;
    fallbackImage.alt = this.#items[index].alt;
    fallbackImage.srcset = this.#items[index].srcset;
    fallbackImage.sizes = '100vw';
    fallbackImage.src = this.#items[index].src;
    if (!dialog.open) dialog.showModal();
    this.#syncScrollLock();
    this.#updateSelection();
    closeButton.focus({ preventScroll: true });

    try {
      const { default: PhotoSwipe } = await loadPhotoSwipe();
      if (token !== this.#opening || !this.isConnected || !dialog.open) return;
      const reduced = prefersReducedMotion();
      const viewer = new PhotoSwipe({
        dataSource: this.#items,
        index: this.#index,
        appendToEl: dialog,
        bgOpacity: 1,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: (levels) => Math.min(1, levels.fit * 2.5),
        maxZoomLevel: 1,
        showHideAnimationType: 'fade',
        showAnimationDuration: reduced ? 0 : 180,
        hideAnimationDuration: reduced ? 0 : 160,
        zoomAnimationDuration: reduced ? 0 : 220,
        getViewportSizeFn: () => ({ x: dialog.clientWidth, y: dialog.clientHeight }),
        paddingFn: () => ({
          top: this.refs.header.offsetHeight,
          bottom: this.refs.footer.hidden ? this.refs.header.offsetHeight : this.refs.footer.offsetHeight,
          left: 0,
          right: 0,
        }),
        close: false,
        zoom: false,
        arrowPrev: false,
        arrowNext: false,
        counter: false,
        trapFocus: false,
        returnFocus: false,
        escKey: false,
        arrowKeys: false,
        closeOnVerticalDrag: false,
        pinchToClose: false,
        imageClickAction: 'zoom',
        bgClickAction: false,
        tapAction: false,
        doubleTapAction: 'zoom',
        preload: [1, 1],
      });
      this.#viewer = viewer;
      // The native modal owns keyboard navigation, including its Tab order.
      viewer.on('keydown', (event) => event.preventDefault());
      viewer.addFilter('contentErrorElement', (_, content) => {
        const link = document.createElement('a');
        link.className = 'pswp__error-msg';
        link.href = content.data.src;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = dialog.getAttribute('aria-label');
        return link;
      });
      viewer.on('change', () => {
        this.#index = viewer.currIndex;
        // Recycled PhotoSwipe holders may retain an old aria-hidden value.
        viewer.element.querySelectorAll('.pswp__item').forEach((holder) => {
          holder.setAttribute('aria-hidden', String(holder !== viewer.currSlide?.holderElement));
        });
        this.#updateSelection();
      });
      viewer.on('zoomPanUpdate', () => {
        const slide = viewer.currSlide;
        zoomButton.hidden = !slide?.isZoomable() || slide.zoomLevels.secondary <= slide.zoomLevels.initial + 0.01;
        zoomButton.setAttribute('aria-pressed', String(!!slide && slide.currZoomLevel > slide.zoomLevels.initial + 0.01));
      });
      viewer.on('openingAnimationEnd', () => {
        // PhotoSwipe cannot close until opening finishes. Let its bindEvents run first.
        queueMicrotask(() => {
          if (this.#closePending || !this.isConnected) viewer.close();
        });
      });
      viewer.on('destroy', () => {
        queueMicrotask(() => {
          if (this.#viewer !== viewer) return;
          this.#viewer = undefined;
          this.#finishClose();
        });
      });
      viewer.init();
      viewer.element.removeAttribute('role');
      viewer.element.removeAttribute('aria-label');
      fallback.hidden = true;
    } catch (error) {
      // The native modal remains usable if the optional gesture module cannot load.
      const failedViewer = this.#viewer;
      this.#viewer = undefined;
      if (failedViewer) {
        // In pinned 5.4.4, destroy otherwise waits for an opening animation that
        // may never finish after an initialization error. Enter its teardown path.
        failedViewer.isDestroying = true;
        failedViewer.destroy();
      }
      console.warn('Product image viewer could not initialize.', error);
    }
  }

  #cancel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.close();
  };

  #keyDown = (event) => {
    if (event.key === 'Escape') this.#cancel(event);
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      event.key === 'ArrowLeft' ? this.previous() : this.next();
    }
  };

  #nativeClose = () => {
    // Native close events are queued; an earlier event can arrive after reopening.
    if (this.refs.dialog.open) return;
    if (this.#viewer) this.close();
    this.#syncScrollLock();
  };

  close() {
    this.#opening++;
    this.#closePending = true;
    if (this.#viewer) this.#viewer.close();
    else this.#finishClose();
  }

  previous() { this.select((this.#index - 1 + this.#items.length) % this.#items.length); }
  next() { this.select((this.#index + 1) % this.#items.length); }
  toggleZoom() { this.#viewer?.toggleZoom(); }

  /** @param {number} index */
  select(index) {
    if (this.#closePending || !this.#items[index]) return;
    if (this.#viewer) this.#viewer.goTo(index);
    else {
      this.#index = index;
      this.refs.fallbackImage.alt = this.#items[index].alt;
      this.refs.fallbackImage.srcset = this.#items[index].srcset;
      this.refs.fallbackImage.src = this.#items[index].src;
      this.#updateSelection();
    }
  }

  #updateSelection() {
    const dots = this.refs.dots ?? [];
    dots.forEach((dot, index) => {
      if (index === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const selected = dots[this.#index];
    selected?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    this.refs.status.textContent = selected?.getAttribute('aria-label') ?? '';
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#items[this.#index].originalIndex));
  }

  #syncScrollLock() {
    document.documentElement.toggleAttribute('scroll-lock', !!document.querySelector(
      'dialog.product-lightbox[open], dialog[scroll-lock][open], details[scroll-lock][open]'
    ));
  }

  async #finishClose() {
    const { dialog, fallbackImage } = this.refs;
    dialog.close();
    fallbackImage.removeAttribute('src');
    fallbackImage.removeAttribute('srcset');
    this.#syncScrollLock();
    window.dispatchEvent(new DialogCloseEvent());
    const gallery = this.closest('media-gallery');
    await gallery?.slideshow?.whenSettled();
    if (!this.isConnected || dialog.open) return;
    const index = this.#items[this.#index]?.originalIndex ?? 0;
    const triggers = [
      gallery?.querySelector(`.product-media__zoom-trigger[data-media-index="${index}"]`),
      gallery?.media?.[index]?.querySelector('.product-media-container__zoom-button'),
    ];
    for (const trigger of triggers) {
      if (trigger instanceof HTMLElement && trigger.getClientRects().length) {
        trigger.focus({ preventScroll: true });
        break;
      }
    }
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
